import { useMemo, useState } from 'react';
import { WorkspaceGrid } from '@/components/grid';
import { useEntityData } from '@/hooks/useEntityData';
import type { ListColumnDSL } from '@/lib/use-view-template';

interface RowEditableSublistProps {
  slug: string;
  title: string;
  filters: Record<string, string>;
  pageSize?: number;
  emptyText?: string;
  onRowClick?: (row: Record<string, unknown>) => void;
  toolbarExtra?: React.ReactNode;
  columns?: ListColumnDSL[];
  editable?: boolean;
}

export function RowEditableSublist({
  slug,
  title,
  filters,
  pageSize = 20,
  emptyText,
  onRowClick,
  toolbarExtra,
  columns,
  editable = true,
}: RowEditableSublistProps) {
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const { meta, rows, total, page, setPage, loading, refresh, template } = useEntityData({
    slug,
    viewType: 'list',
    limit: pageSize,
    sort,
    order,
    filters,
  });

  const resolvedColumns = useMemo(
    () => columns ?? template?.columns ?? [],
    [columns, template?.columns],
  );

  return (
    <div className="space-y-3 rounded-lg border bg-background p-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold">{title}</div>
          <div className="text-xs text-muted-foreground">{total} rows</div>
        </div>
        {toolbarExtra}
      </div>

      {!meta ? (
        <div className="py-6 text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 && !loading ? (
        <div className="py-6 text-sm text-muted-foreground">{emptyText ?? 'No records'}</div>
      ) : (
        <WorkspaceGrid
          slug={slug}
          meta={meta}
          columns={resolvedColumns}
          data={rows}
          total={total}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onSort={(field, nextOrder) => {
            setSort(field);
            setOrder(nextOrder);
          }}
          sortField={sort}
          sortOrder={order}
          onRefresh={refresh}
          onRowClick={onRowClick}
          selectable={false}
          editable={editable}
          showDensity={false}
          showSavedViews={false}
          toolbarExtra={null}
          loading={loading}
        />
      )}
    </div>
  );
}
